import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { Loader } from "lucide-react";
import { Card } from "@/components/ui/card";

export interface ProcessingIndicator {
  /** Short label for the running work, e.g. "Underwriting Analysis". */
  title: string;
  /** Shown only while this is the sole running process. */
  description?: string;
  /** Wall-clock start; the toast ticks elapsed from this so it does not wait on later WS snapshots. */
  startedAt?: string | null;
  elapsedSeconds?: number | null;
}

type RegisteredIndicator = ProcessingIndicator & { id: string };

type ProcessingStore = {
  register: (id: string, indicator: ProcessingIndicator) => void;
  unregister: (id: string) => void;
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => RegisteredIndicator[];
};

const EMPTY_SNAPSHOT: RegisteredIndicator[] = [];

function createProcessingStore(): ProcessingStore {
  const registered = new Map<string, ProcessingIndicator>();
  const listeners = new Set<() => void>();
  let snapshot: RegisteredIndicator[] = EMPTY_SNAPSHOT;

  const publish = () => {
    snapshot =
      registered.size === 0
        ? EMPTY_SNAPSHOT
        : [...registered].map(([id, indicator]) => ({ id, ...indicator }));
    listeners.forEach((listener) => listener());
  };

  const isUnchanged = (current: ProcessingIndicator | undefined, next: ProcessingIndicator) =>
    current != null &&
    current.title === next.title &&
    current.description === next.description &&
    current.startedAt === next.startedAt &&
    current.elapsedSeconds === next.elapsedSeconds;

  return {
    register(id, indicator) {
      if (isUnchanged(registered.get(id), indicator)) return;
      registered.set(id, indicator);
      publish();
    },
    unregister(id) {
      if (registered.delete(id)) publish();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getSnapshot() {
      return snapshot;
    },
  };
}

const ProcessingStoreContext = createContext<ProcessingStore | null>(null);

function useProcessingStore(): ProcessingStore {
  const store = useContext(ProcessingStoreContext);
  if (!store) {
    throw new Error("useProcessingIndicator requires ProcessingToastProvider");
  }
  return store;
}

/**
 * Publishes one in-flight process to the page toast for as long as `indicator`
 * is non-null. Callers must stay mounted with the page (not a lazy tab panel).
 */
export function useProcessingIndicator(indicator: ProcessingIndicator | null): void {
  const store = useProcessingStore();
  const id = useId();

  useEffect(() => {
    if (indicator) store.register(id, indicator);
    else store.unregister(id);
  }, [id, indicator, store]);

  useEffect(() => () => store.unregister(id), [id, store]);
}

function elapsedSecondsOf(indicator: ProcessingIndicator, nowMs: number): number | null {
  if (indicator.startedAt) {
    const start = Date.parse(indicator.startedAt);
    if (!Number.isNaN(start)) return Math.max(0, (nowMs - start) / 1000);
  }
  return indicator.elapsedSeconds ?? null;
}

const elapsedLabel = (indicator: ProcessingIndicator, nowMs: number) => {
  const seconds = elapsedSecondsOf(indicator, nowMs);
  return seconds == null ? null : `${Math.round(seconds)}s elapsed`;
};

const ProcessingToast: React.FC = () => {
  const store = useProcessingStore();
  const indicators = useSyncExternalStore(store.subscribe, store.getSnapshot, () => EMPTY_SNAPSHOT);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const needsTick = indicators.some((indicator) => Boolean(indicator.startedAt));

  useEffect(() => {
    if (!needsTick) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [needsTick]);

  if (indicators.length === 0) return null;

  const [first] = indicators;
  const isCombined = indicators.length > 1;
  const firstElapsed = elapsedLabel(first, nowMs);

  return createPortal(
    <>
      {/* Outside the live region below: its text is not status for a reader. */}
      <style>{`
        @keyframes processing-toast-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(350%); }
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        data-testid="processing-toast"
        className="pointer-events-none fixed bottom-[calc(var(--assistant-lip-cover)_+_1rem)] right-4 z-toast w-80 max-w-[calc(100vw-2rem)]"
      >
        <Card className="border-primary/20 bg-background p-4 shadow-lg">
          <div className="mb-3 flex items-start gap-3">
            <Loader className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-primary" />
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold">{isCombined ? "Processing" : first.title}</h2>
              {isCombined ? (
                <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                  {indicators.map((indicator) => {
                    const elapsed = elapsedLabel(indicator, nowMs);
                    return (
                      <li key={indicator.id} className="tabular-nums">
                        {indicator.title}
                        {elapsed ? ` — ${elapsed}` : null}
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <>
                  {first.description && (
                    <p className="mt-1 text-xs text-muted-foreground">{first.description}</p>
                  )}
                  {firstElapsed && (
                    <p className="mt-1 text-xs tabular-nums text-muted-foreground">
                      {firstElapsed}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full w-1/3 rounded-full bg-primary"
              style={{ animation: "processing-toast-slide 1.2s ease-in-out infinite" }}
            />
          </div>
        </Card>
      </div>
    </>,
    document.body
  );
};

/**
 * Page-scoped store plus the single toast portal. Mount once on the page that
 * owns loading lanes; publishers register through `useProcessingIndicator`.
 */
export const ProcessingToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const storeRef = useRef<ProcessingStore | null>(null);
  if (storeRef.current == null) storeRef.current = createProcessingStore();

  return (
    <ProcessingStoreContext.Provider value={storeRef.current}>
      {children}
      <ProcessingToast />
    </ProcessingStoreContext.Provider>
  );
};

export default ProcessingToastProvider;
